import { useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { useSurvey } from '../store/surveyStore.js'
import { MYCULTURE_PREF } from '../surveys/myculture-pref.js'
import SurveyShell from '../components/survey/SurveyShell.jsx'
import ComparisonStep from '../components/survey/ComparisonStep.jsx'
import DemographicsStep from '../components/survey/Demographics.jsx'
import myCultureLogo from '../assets/myculture.jpg'

const TOTAL_STEPS = 47

export default function MyCultureFlow() {
  const { accessId } = useParams()
  const nav = useNavigate()
  const survey = useSurvey()
  const [validation, setValidation] = useState(null)

  useEffect(() => {
    survey.load(accessId)
    return () => survey.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessId])

  if (survey.status === 'idle') return <div className="text-center py-12 text-ink-soft">Lade…</div>
  if (survey.status === 'completed') return <Navigate to={`/survey/${accessId}/result`} replace />

  const goNext = async () => {
    setValidation(null)
    const next = survey.step + 1
    if (next > TOTAL_STEPS) return
    survey.setStep(next)
    await survey.save()
  }
  const goBack = async () => {
    setValidation(null)
    if (survey.step <= 0) return
    survey.setStep(survey.step - 1)
    await survey.save()
  }
  const onFinish = async () => {
    setValidation(null)
    await survey.finish()
    nav(`/survey/${accessId}/result`)
  }

  const step = survey.step
  const data = survey.data
  const answers = data.answers || {}
  const shellProps = { logoSrc: myCultureLogo, logoAlt: 'myculture' }

  // 0: Welcome
  if (step === 0) {
    return (
      <SurveyShell
        step={0} total={TOTAL_STEPS} hideBack
        title={MYCULTURE_PREF.welcome.headline}
        intro={null}
        onNext={goNext}
        nextLabel="Start"
        {...shellProps}
      >
        <h2 className="text-xl text-ink-soft -mt-2 mb-4">{MYCULTURE_PREF.welcome.subheadline}</h2>
        {MYCULTURE_PREF.welcome.body.map((p, i) => (
          <p key={i} className="text-ink leading-relaxed mb-3">{p}</p>
        ))}
      </SurveyShell>
    )
  }

  // 1: Demographics
  if (step === 1) {
    return (
      <DemographicsStep
        step={1} total={TOTAL_STEPS}
        data={data.demographics}
        onChange={(patch) => survey.setDemographics(patch)}
        onBack={goBack} onNext={goNext}
        validation={validation} setValidation={setValidation}
        {...shellProps}
      />
    )
  }

  // 2..47: from spec (intros + comparisons)
  const stepDef = MYCULTURE_PREF.steps[step]
  if (!stepDef) {
    return <SurveyShell step={step} total={TOTAL_STEPS} {...shellProps}>
      <div className="text-rose-700">Unbekannter Schritt: {step}</div>
    </SurveyShell>
  }

  const isLast = step === TOTAL_STEPS

  if (stepDef.kind === 'intro') {
    return (
      <SurveyShell
        step={step} total={TOTAL_STEPS}
        intro={stepDef.body}
        onBack={goBack} onNext={goNext}
        {...shellProps}
      >
        <div className="text-center text-ink-mute pt-2">→</div>
      </SurveyShell>
    )
  }

  if (stepDef.kind === 'comparison') {
    const value = answers[stepDef.key] || 0
    const requireAndAdvance = () => {
      if (!value) { setValidation('Bitte nehmen Sie eine Bewertung vor.'); return }
      setValidation(null)
      if (isLast) onFinish()
      else goNext()
    }
    return (
      <SurveyShell
        step={step} total={TOTAL_STEPS}
        title="Bitte nehmen Sie Ihre Einschätzung vor."
        onBack={goBack}
        onNext={requireAndAdvance}
        nextLabel={isLast ? 'Abschließen' : 'Weiter'}
        nextVariant={isLast ? 'primary' : 'secondary'}
        validation={validation}
        {...shellProps}
      >
        <ComparisonStep
          prompt={stepDef.prompt}
          options={stepDef.options}
          scale={stepDef.scale || 'twoway'}
          value={value}
          onChange={(v) => survey.setAnswer(stepDef.key, v)}
          scaleLabels={MYCULTURE_PREF.scaleLabels}
          hint={MYCULTURE_PREF.copy.pickHint}
        />
      </SurveyShell>
    )
  }

  return <div>Unsupported step kind: {stepDef.kind}</div>
}
